"use client";

import { useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  CoordinatePlane,
  PlanePoint,
} from "@/components/tools/math/coordinate-plane";
import { useToolNumber } from "@/components/tools/tool-ui";
import { downloadPng } from "@/lib/tool-kit/download";
import { samplePath, type Viewport } from "@/lib/tool-kit/math/plot";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/*
 * A graph of one or more functions of x with a few marked points — the linear solver's
 * "each side as a line" picture and the system solver's pair of lines. Text comes in as
 * props, so the widgets keep every visible word in their own strings.
 */

export interface GraphLine {
  /** y as a function of x; omit for a vertical line. */
  fn?: (x: number) => number;
  /** For a vertical line: its x. */
  vertical?: number;
  label: string;
}

const LINE_COLORS = [
  CHART_COLORS[0],
  CHART_COLORS[3],
  CHART_COLORS[2],
  CHART_COLORS[5],
] as const;

export interface GraphMark {
  x: number;
  y: number;
  label?: string;
}

/** A window around `center` (and the origin when close) that holds every line there. */
export function fitWindow(
  center: number,
  fns: ((x: number) => number)[],
  points: number[] = []
): Viewport {
  const half = Math.max(5, Math.abs(center) * 1.5);
  const xMin = center - half;
  const xMax = center + half;
  const values: number[] = [...points];
  for (const fn of fns) {
    for (const x of [xMin, xMax, center]) {
      const y = fn(x);
      if (Number.isFinite(y)) {
        values.push(y);
      }
    }
  }
  let yMin = Math.min(...values, 0);
  let yMax = Math.max(...values, 0);
  if (yMax - yMin < 2) {
    yMin -= 1;
    yMax += 1;
  }
  const pad = (yMax - yMin) * 0.1;
  return { xMin, xMax, yMin: yMin - pad, yMax: yMax + pad };
}

function linePath(
  line: GraphLine,
  viewport: Viewport,
  scale: { x: (v: number) => number; y: (v: number) => number }
): string {
  if (line.vertical !== undefined) {
    const x = scale.x(line.vertical);
    return `M${x} ${scale.y(viewport.yMin)} L${x} ${scale.y(viewport.yMax)}`;
  }
  return line.fn ? samplePath(line.fn, viewport, scale.x, scale.y, 60) : "";
}

export function LinesGraph({
  title,
  lines,
  marks,
  viewport,
  downloadLabel,
  fileName,
}: {
  title: string;
  lines: GraphLine[];
  marks: GraphMark[];
  viewport: Viewport;
  downloadLabel: string;
  fileName: string;
}) {
  const fmt = useToolNumber();
  const ref = useRef<SVGSVGElement>(null);
  return (
    <div className="space-y-3">
      <CoordinatePlane
        formatTick={(value, decimals) =>
          fmt(value, { maximumFractionDigits: decimals })
        }
        svgRef={ref}
        title={title}
        viewport={viewport}
      >
        {(scale) => (
          <>
            {lines.map((line, index) => (
              <path
                d={linePath(line, viewport, scale)}
                fill="none"
                key={line.label}
                stroke={LINE_COLORS[index % LINE_COLORS.length]}
                strokeWidth={2.5}
              />
            ))}
            {marks.map((mark) => (
              <PlanePoint
                color={CHART_COLORS[2]}
                cx={scale.x(mark.x)}
                cy={scale.y(mark.y)}
                key={`${mark.x}:${mark.y}`}
                label={mark.label}
              />
            ))}
          </>
        )}
      </CoordinatePlane>
      <ul className="flex flex-wrap gap-x-5 gap-y-1 text-muted-foreground text-sm">
        {lines.map((line, index) => (
          <li className="flex items-center gap-2" key={line.label}>
            <span
              aria-hidden="true"
              className="inline-block h-1 w-5 rounded"
              style={{
                backgroundColor: LINE_COLORS[index % LINE_COLORS.length],
              }}
            />
            <span className="font-mono">{line.label}</span>
          </li>
        ))}
      </ul>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng(fileName, ref.current).catch(() => undefined);
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}
